import { Fragment, useState } from "react";
import moment from "moment/moment";
import "moment/locale/ar";
import CalendarHeader from "./calendar-helpers/calender-header/CalendarHeader";
import { useIsMobile } from "@/hooks/useIsMobile";
import WeekDays from "./calendar-helpers/WeekDays";
import DayAppointmentsBox from "./calendar-helpers/day-appointments-box/DayAppointmentsBox";
import DayCell, { SlotStatus } from "./calendar-helpers/DayCell";
import { dateFormat, useGetTimeSlots } from "@/api/AppApi/services/time-slots";
import GeneralApiLoader from "../../../lib/loaders/GeneralApiLoader";
import { ICommonProductDetails } from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { useLang } from "@/providers/langProvider";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { IuseProductHasAttrsResult } from "@/hooks/product-has-attrs/product-has-attrs-helpers/interfaces";

interface Props {
  beside?: boolean;
  product?: ICommonProductDetails;
  form: UseFormReturn<FieldValues, any>;
  productState?: IuseProductHasAttrsResult;
}

const Calendar: React.FC<Props> = ({
  beside = false,
  product,
  form,
  productState,
}) => {
  const locale = useLang();
  const [dateObject, setDateObject] = useState(moment());
  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null);
  const notAvailable = product?.id === undefined;
  moment.updateLocale("en", {
    week: {
      dow: 6, // First day of week is Saturday
      doy: 12, // First week of year must contain 1 January (7 + 6 - 1)
    },
  });
  moment.locale(locale);

  const mobile = useIsMobile();
  const DetaileInCalendar = mobile || !beside;
  const getTimeSlots = useGetTimeSlots({
    product_id: productState?.currentProd?.id! || product?.id!,
    from_date: moment(dateObject)
      .startOf("month")
      .locale("en")
      .format(dateFormat),
    to_date: moment(dateObject).endOf("month").locale("en").format(dateFormat),
  });
  const firstDayOfMonth = moment(dateObject).startOf("month").get("day") + 1; // Day of week 0..6

  const isSelectedDateInCurrentMonth =
    !!selectedDate &&
    moment(selectedDate).get("year") === moment(dateObject).get("year") &&
    moment(selectedDate).get("month") === moment(dateObject).get("month");
  const isSelectedDateInWeek = (index: number, shift: number): boolean => {
    return (
      isSelectedDateInCurrentMonth &&
      index + 1 === Math.ceil((+moment(selectedDate).get("date") + shift) / 7)
    );
  };

  const getFullDate = (d: number) => moment(dateObject).set("date", d);
  const isToday = (d: number) => moment(getFullDate(d)).isSame(moment(), "day");
  const isDateSelected = (d: number) =>
    moment(getFullDate(d)).isSame(selectedDate, "day");
  const getDateStatus = (d: number) => {
    const isPreviousDate = (d: number) =>
      moment(getFullDate(d)).isBefore(moment(), "day");
    if (isPreviousDate(d)) return SlotStatus.disabled;
    const slot = getTimeSlots.data?.result?.find(
      (timeSlot) =>
        timeSlot.date ===
        moment(getFullDate(d)).locale("en").format(dateFormat),
    );
    if (!slot) return SlotStatus.disabled;
    if (slot.is_booked) return SlotStatus.booked;
    return SlotStatus.available;
  };

  const blankCells = (num: number, uniquKey: string) => {
    let blanks = [];
    for (let i = 0; i < num; i++) {
      blanks.push(
        <td key={i + uniquKey}>
          <DayCell blank />
        </td>,
      );
    }
    return blanks;
  };

  const startBlanks = blankCells(firstDayOfMonth, "start");
  let daysInCurrMonth = [];

  for (let d = 1; d <= dateObject.daysInMonth(); d++) {
    daysInCurrMonth.push(
      <td key={d}>
        <DayCell
          day={d}
          today={isToday(d)}
          selected={isDateSelected(d)}
          onClick={() => setSelectedDate(getFullDate(d))}
          status={getDateStatus(d)}
        />
      </td>,
    );
  }
  const rem = (startBlanks.length + daysInCurrMonth.length) % 7;
  const endBlanks = rem !== 0 ? blankCells(7 - rem, "end") : [];
  let rows: any = [];
  let row: any = [];
  const totalSlots = [...startBlanks, ...daysInCurrMonth, ...endBlanks];

  totalSlots.forEach((slot, i) => {
    row.push(slot);
    if ((i + 1) % 7 === 0) {
      rows.push(row);
      row = [];
    }
  });

  const currCalendarPage = rows.map((w: any, index: number) => {
    return (
      <Fragment key={index}>
        <tr>{w}</tr>
        {DetaileInCalendar && (
          <tr>
            <td colSpan={7} style={{ padding: 0 }}>
              <DayAppointmentsBox
                form={form}
                product={product!}
                show={isSelectedDateInWeek(index, startBlanks.length)}
                date={selectedDate}
                onClose={() => setSelectedDate(null)}
                beside={false}
                productState={productState}
              />
            </td>
          </tr>
        )}
      </Fragment>
    );
  });
  return (
    <>
      <div
        className="date-calendar"
        style={notAvailable ? { opacity: 0.3 } : {}}
      >
        <CalendarHeader dateObject={dateObject} setDateObject={setDateObject} />
        <table>
          <thead>
            <WeekDays />
          </thead>
          <tbody>
            {!notAvailable && getTimeSlots.isLoading ? (
              <tr>
                <td colSpan={7}>
                  <GeneralApiLoader />
                </td>
              </tr>
            ) : (
              currCalendarPage
            )}
          </tbody>
        </table>
      </div>
      {!DetaileInCalendar && (
        <DayAppointmentsBox
          form={form}
          product={product!}
          show={!!selectedDate}
          date={selectedDate}
          onClose={() => setSelectedDate(null)}
          beside
        />
      )}
    </>
  );
};
export default Calendar;
